// The weekly estimate (notifications overhaul, slice 21). `GET
// /v1/me/notification-estimate` replays the caller's last 7 days through their
// CURRENT settings and answers how many emails that would have been, plus the
// same count under each preset, so the page can show what a preset would do
// before it is chosen.
//
// Per (user, account), like the settings it is computed from, so the query key
// carries the active account. Parsed, not cast: a body missing `presets` would
// paint "about undefined a week" on three buttons.
//
// `truncated` says the event read hit its row cap, so EVERY total here is a
// floor — the header and the preset buttons word their numbers "at least {n}"
// while it is set (`NotificationEstimateWireSchema`, `@developerz/domain`).

import { type NotificationEstimateWire, NotificationEstimateWireSchema } from '@developerz/domain';
import { createQuery, type QueryClient } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

// The DOMAIN's own wire schema (`packages/domain/src/wire/notification.ts`),
// re-exported under the name this app's calls and tests use — not a second,
// looser declaration that could drift from the server's shape. The literals are
// load-bearing: the window IS 7 days and every total IS an integer, so a
// fixture that says otherwise fails the parse rather than painting it.
export const WeeklyEstimateSchema = NotificationEstimateWireSchema;

export type WeeklyEstimate = NotificationEstimateWire;

/** One number per preset, keyed by `NotificationPreset`. */
export type PresetEstimates = WeeklyEstimate['presets'];

export const NOTIFICATION_ESTIMATE_QUERY_KEY = 'notification_estimate';

/** The caller's weekly estimate. Idle with no session, like `useNotificationSettings`. */
export function useNotificationEstimate(token: string | null = SESSION_TOKEN) {
  return createQuery<WeeklyEstimate>(() => ({
    queryKey: [NOTIFICATION_ESTIMATE_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    queryFn: () =>
      apiFetch('/v1/me/notification-estimate', token, undefined, { schema: WeeklyEstimateSchema }),
  }));
}

/**
 * Re-read the estimate. Called from `onSettled` of EVERY settings write: the
 * number is a function of the settings, so it is stale the moment one changes.
 * Resolves once the re-read has landed, so a caller awaiting it inside
 * `createMutation`'s pending window keeps its control held until the header is
 * true again.
 */
export function refreshNotificationEstimate(client: QueryClient): Promise<void> {
  return client.invalidateQueries({ queryKey: [NOTIFICATION_ESTIMATE_QUERY_KEY] });
}

/**
 * The per-preset numbers to print on the preset buttons, or `undefined` when
 * there is nothing honest to print: the read has not landed, or there is no
 * history. "about 0 a week" three times over would be three placeholder metrics
 * (`docs/standards/design-system.md` § Enterprise posture).
 */
export function presetEstimates(estimate: WeeklyEstimate | undefined): PresetEstimates | undefined {
  return estimate !== undefined && estimate.events > 0 ? estimate.presets : undefined;
}
